import {
  AnimatedSprite,
  Application,
  Assets,
  Container,
  Rectangle,
  Texture,
} from "pixi.js";
import { Position } from "types/battle-engine/battle";
import { VFX_SPRITE_FRAME_ANCHOR_DATA } from "@/data/vfx-sprite-frame-anchor-data";

export interface ParticleConfig {
  app: Application;
  spriteUrl: string;
  spriteFrameCount: number;
  spriteScale?: number;
  from: Position;
  to: Position;
  speed: number; // pixels per second
  parent?: Container;
  shouldAlignToDestination?: boolean;
}

const DEFAULT_FPS = 24;

/**
 * Particle class handles visual particle rendering
 * - Moves from start to end position at specified speed
 * - Fades out as it approaches destination (accelerating fade)
 * - Auto-destroys on completion
 * - Supports animated sprite strips
 * - Optional rotation toward destination
 */
export class Particle {
  private sprite: AnimatedSprite | null = null;
  private container: Container;
  private startPos: Position;
  private endPos: Position;
  private speed: number;
  private duration: number;
  private startTime: number;
  private app: Application;
  private parent: Container;
  private isDestroyed: boolean = false;
  private tickerFn: ((time: any) => void) | null = null;
  private shouldAlignToDestination: boolean;

  constructor(config: ParticleConfig) {
    this.app = config.app;
    this.parent = config.parent ?? config.app.stage;
    this.startPos = config.from;
    this.endPos = {
      x: config.to.x,
      y: config.to.y,
    };
    this.speed = config.speed;
    this.shouldAlignToDestination = config.shouldAlignToDestination ?? false;

    // Calculate duration based on distance and speed
    const distance = Math.sqrt(
      Math.pow(config.to.x - config.from.x, 2) +
        Math.pow(config.to.y - config.from.y, 2),
    );
    this.duration = (distance / config.speed) * 1000; // Convert to ms
    this.startTime = 0; // Will be set when update loop starts

    // Create container
    this.container = new Container();
    this.container.x = config.from.x;
    this.container.y = config.from.y;
    this.container.zIndex = config.from.y;
    this.container.pivot.set(0.5);

    // Load sprite and start animation
    this.initSprite(
      config.spriteUrl,
      config.spriteFrameCount,
      config.spriteScale,
    );

    // Add to parent container
    this.parent.addChild(this.container);
  }

  /**
   * Load particle sprite and set up initial rotation
   */
  private async initSprite(
    spriteUrl: string,
    spriteFrameCount: number,
    spriteScale?: number,
  ) {
    try {
      const texture = await Assets.load<Texture>({
        src: spriteUrl,
        data: {
          resourceOptions: { crossorigin: "anonymous" },
          loadOptions: { fetch: { mode: "cors", credentials: "omit" } },
        },
      });

      const frames = this.createFrames(texture, spriteFrameCount);

      this.sprite = new AnimatedSprite(frames);

      const anchor = VFX_SPRITE_FRAME_ANCHOR_DATA[spriteUrl] ?? {
        x: 0.5,
        y: 0.5,
      };
      this.sprite.anchor.set(anchor.x, anchor.y);

      this.sprite.scale.set(spriteScale ?? 1);
      this.sprite.loop = true;
      this.sprite.animationSpeed = DEFAULT_FPS / 60;

      // Calculate initial rotation if needed
      if (this.shouldAlignToDestination) {
        this.updateRotation();
      }

      this.container.addChild(this.sprite);
      this.sprite.play();

      // Start update loop
      this.startUpdate();
    } catch (error) {
      console.error("Failed to load particle sprite:", spriteUrl, error);
      this.destroy();
    }
  }

  /**
   * Create animation frames from sprite strip
   */
  private createFrames(texture: Texture, frameCount: number): Texture[] {
    if (frameCount === 1) {
      return [texture];
    }

    const frames: Texture[] = [];
    const frameWidth = texture.width / frameCount;
    const frameHeight = texture.height;

    for (let i = 0; i < frameCount; i++) {
      const frame = new Texture({
        source: texture.source,
        frame: new Rectangle(i * frameWidth, 0, frameWidth, frameHeight),
      });
      frames.push(frame);
    }

    return frames;
  }

  /**
   * Update rotation to point toward destination
   */
  private updateRotation() {
    if (!this.sprite) return;

    const dx = this.endPos.x - this.container.x;
    const dy = this.endPos.y - this.container.y;

    // Calculate angle in radians
    this.sprite.rotation = Math.atan2(dy, dx);
  }

  /**
   * Start the update ticker for position interpolation and fade
   */
  private startUpdate() {
    this.startTime = performance.now();

    this.tickerFn = (time: any) => {
      if (this.isDestroyed) return;

      const now = performance.now();
      const elapsed = now - this.startTime;
      const t = Math.min(1, elapsed / this.duration);

      // Linear interpolation for position
      this.container.x = Math.round(
        this.startPos.x + (this.endPos.x - this.startPos.x) * t,
      );
      this.container.y = Math.round(
        this.startPos.y + (this.endPos.y - this.startPos.y) * t,
      );

      // Accelerating fade: fade faster as it approaches destination
      // Using quadratic easing: (1 - t)^2
      const fadeProgress = 1 - Math.pow(1 - t, 4);
      this.container.alpha = t > 0.665 ? 1 - fadeProgress : 1;

      // Update rotation if needed
      if (this.shouldAlignToDestination) {
        this.updateRotation();
      }

      // Update zIndex
      this.container.zIndex = this.container.y;

      // Manually update the sprite animation
      if (this.sprite && this.sprite.playing) {
        this.sprite.update(time);
      }

      // Check completion
      if (t >= 1) {
        this.destroy();
      }
    };

    this.app.ticker.add(this.tickerFn);
  }

  /**
   * Clean up resources and remove from stage
   */
  public destroy() {
    if (this.isDestroyed) return;
    this.isDestroyed = true;

    if (this.tickerFn) {
      this.app.ticker.remove(this.tickerFn);
      this.tickerFn = null;
    }

    this.container.destroy({ children: true });
  }
}
